import type { Data, DataProp, DictionaryItem, ImmutableArray, ImmutableDictionary, LeafKey, Nullish, Query, Validator, ValidatorsType } from "shelving";
import { getData, getDictionary, getFirstItem, getUniqueArray, getWords, isString, simplifyString } from "shelving";

/**
 * Keywords stored on an item, e.g. `["running", "challenge"]`
 * - These are usually extracted from a title or location field.
 * - These are 'double stored' — the array format is the canonical correct format and the map format is a duplicate.
 * - Firestore can only query on one 'contains' query at once, so to query for items containing 2+ keywords we use the map format.
 * - When querying with the map format the results cannot be sorted, but this is acceptable as a temporary workaround to save time.
 */
export const KEYWORD_FIELDS: {
	readonly keywords: Validator<ImmutableArray<string>>;
	readonly keyword: Validator<ImmutableDictionary<true>>;
} = {
	keywords: { validate: (v: unknown) => v as ImmutableArray<string> }, // Validation is unnecessary for this as it's generated on server only.
	keyword: { validate: (v: unknown) => v as ImmutableDictionary<true> }, // Validation is unnecessary for this as it's generated on server only.
};
export type KeywordData = ValidatorsType<typeof KEYWORD_FIELDS>;
export const EMPTY_KEYWORDS: KeywordData = { keywords: [], keyword: {} };

/** Apply filters to a query. */
export function getKeywordFilters(words: ImmutableArray<string>): Query<KeywordData> {
	if (words.length < 2) {
		// Search using keywords array, which allows ordering (and thus pagination).
		return { "keywords[]": getFirstItem(words) };
	} else {
		// Search using keywords object, which allows multiple words but no ordering (and no pagination).
		return getData(words.map(_getKeywordFilter));
	}
}
const _getKeywordFilter = (word: string): DataProp<Query<KeywordData>> => [`keyword.${word}`, true];

export function getKeywordQuery<T extends Data & KeywordData>(
	words: ImmutableArray<string>, //
	order?: `${LeafKey<T>}` | `!${LeafKey<T>}` | ImmutableArray<`${LeafKey<T>}` | `!${LeafKey<T>}`>,
): Query<T> {
	return { ...getKeywordFilters(words), $order: words.length < 2 ? order : undefined };
}

/**
 * Turn a set of fields into a corresponding set of keyword data.
 * @param fields Content fields. All unique words in this are used as keywords.
 */
export function getKeywordData(...fields: Nullish<string>[]): KeywordData {
	const keywords = getUniqueArray(getWords(fields.filter(isString).join(" ")).map(simplifyString));
	const keyword = getDictionary(_yieldKeywordEntry(keywords));
	return { keywords, keyword };
}
function* _yieldKeywordEntry(keywords: ImmutableArray<string>): Iterable<DictionaryItem<true>> {
	for (const keyword of keywords) yield [keyword, true];
}
